import store, { BaseState } from "@/store"
import { NavigationGuardNext, Route } from "vue-router"
import { Store } from "vuex"
import { LoginState } from "@/types"
import { setSkipAllApiCaches } from "@/services/apiCache"

let refreshPromise: Promise<any> | null = null

/**
 * @summary Navigation guard which sets the skipCache property on all API caches based on the current route.
 * @param to The 'to' location provided by the router.
 * @param from The 'from' location provided by the router.
 * @param next The router 'next' hook.
 */
async function setApiCacheSkip (to: Route, from: Route, next: NavigationGuardNext) {
	const baseRouteChanged = to.matched?.[0]?.path !== from.matched?.[0]?.path
	if (!baseRouteChanged) {
		next()
		return
	}

	const shouldSkipCache = !to.meta?.allowApiCache
	setSkipAllApiCaches(shouldSkipCache)
	next()
}

/**
 * Refreshes the user object in the store if it is not set
 * This ensures if we open in a new tab it will load the user, so we can check if v2 is enabled
 * Does not refresh the user if we're going to the login route as it would cause infinite redirects
 * @param to The 'to' location provided by the router.
 * @param vuexStore An implementation of the vuex store
 */
async function refreshUserInfo (to: Route, vuexStore: Store<BaseState>) {
	// If we're explicitly not logged in (i.e. not waiting for user to select tenant etc.) & trying to access a logged in route
	if (vuexStore.state.loginState === LoginState.NotLoggedIn && to.name !== "home" && to.name !== "login") {
		// If we've not called the action yet, call it
		// If it's already been called, wait for the original action to return rather than calling it again
		if (refreshPromise == null) {
			refreshPromise = vuexStore.dispatch("refreshUserInfo")
		}

		await refreshPromise
		refreshPromise = null
	}
}

/**
 * @summary Navigation guard which redirects users to the dashboard, if they do not have permission to view a page
 * @param to The 'to' location provided by the router.
 * @param _from The 'from' location provided by the router.
 * @param next The router 'next' hook.
 * @param permission The permission to look for on the user, will search the user object & user.extendedPermissions for that value
 * @param vuexStore An implementation of the vuex store (default actual vuex store)
 */
async function hasPermission (to: Route, _from: Route, next: NavigationGuardNext, permission: string, vuexStore: Store<BaseState> = store) {
	await refreshUserInfo(to, vuexStore)
	const me = vuexStore?.state?.me
	// @ts-ignore TODO fix indexing type
	const hasPermission = !!me[permission] || !!me?.extendedPermissions[permission]

	if (!hasPermission) {
		console.error("You do not have permission to view this page. Redirecting to dashboard")
		next({ name: "dashboard" })
	} else {
		next()
	}
}

export { hasPermission, setApiCacheSkip }
